/**
 * Fonte única de verdade do SEO da landing page.
 *
 * É lido por dois mundos, e por isso é CommonJS:
 *  - o app (webpack) → [src/utils/pageMeta.js] e [src/router/index.js], que
 *    aplicam título/description/canonical/OG/JSON-LD a cada navegação;
 *  - o build (node)  → [scripts/generate-seo.js], que escreve o `sitemap.xml`
 *    e o `<head>` estático de cada rota dentro do `dist/`.
 *
 * O head estático existe porque **scraper de WhatsApp, Facebook e LinkedIn não
 * executa JavaScript**: sem ele, o preview de link compartilhado ignora tudo o
 * que o `pageMeta` faz em tempo de execução.
 *
 * ⚠️ Rota nova entra no ROUTES abaixo. Sem entrada aqui, a rota não ganha head
 * estático nem lugar no sitemap — o `generate-seo.js` avisa no build.
 */

const faqDelivery = require('./faq-delivery');
const faqTablet = require('./faq-tablet');

/**
 * Host canônico. Fixo de propósito: o canonical precisa ser o mesmo no HTML
 * gerado no build e no que o app escreve em tempo de execução, senão localhost
 * e ambientes de teste vazam para o índice. (Decisão A: o site fica no `lp.`.)
 */
const SITE = {
  url: 'https://lp.zapermenu.com.br',
  name: 'ZaperMenu',
  legalName: 'Scheeren Company LTDA',
  locale: 'pt_BR',
  defaultImage: '/img/og/home.png',
  logo: '/img/icons/android-chrome-512x512.png',
  sameAs: [
    'https://www.instagram.com/zapermenu',
    'https://www.facebook.com/zapermenu',
  ],
  whatsapp: 'https://wa.me/5549988951210',
};

const INDEXABLE = 'index,follow,max-image-preview:large';
const NOINDEX_FOLLOW = 'noindex,follow';
const NOINDEX_NOFOLLOW = 'noindex,nofollow';

/**
 * Uma entrada por rota do [src/router/index.js].
 *
 * - `robots`      controla indexação (e o que entra no sitemap).
 * - `priority` /
 *   `changefreq`  só valem para rota indexável.
 * - `faq`         alimenta o JSON-LD `FAQPage` — só onde o acordeão aparece
 *                 de fato na tela.
 */
const ROUTES = [
  {
    path: '/',
    title: 'Cardápio Digital e Sistema de Pedidos para Delivery | ZaperMenu',
    description: 'Automatize os pedidos do seu delivery com cardápio digital, comanda digital e impressão automática na cozinha. Integrado ao WhatsApp, sem taxa por pedido.',
    image: '/img/og/home.png',
    robots: INDEXABLE,
    changefreq: 'weekly',
    priority: '1.0',
    faq: faqDelivery,
    software: true,
  },
  {
    path: '/tablet',
    title: 'ZaperMenu Tablet de Mesa — Autoatendimento que aumenta o ticket médio',
    description: 'Cardápio de autoatendimento no tablet da mesa: seu cliente pede sozinho, você reduz mão de obra, elimina erros e vende mais. Funciona nos tablets Android que você já tem.',
    image: '/img/og/tablet.png',
    robots: INDEXABLE,
    changefreq: 'monthly',
    priority: '0.9',
    faq: faqTablet,
    software: true,
  },
  {
    path: '/revenda',
    title: 'Seja Revenda ZaperMenu — Até 80% da primeira mensalidade + recorrente',
    description: 'Revenda autorizada ZaperMenu: receba até 80% da primeira mensalidade de cada cliente e comissão recorrente, que cresce conforme a sua carteira aumenta.',
    robots: INDEXABLE,
    changefreq: 'monthly',
    priority: '0.7',
  },

  // ── Funil: sem valor de busca, e indexá-las só polui o índice e o relatório
  // de conversão. `follow` onde ainda faz sentido seguir os links da página.
  {
    path: '/planos',
    title: 'Planos e preços | ZaperMenu',
    description: 'Conheça os planos do ZaperMenu e fale com um especialista.',
    robots: NOINDEX_FOLLOW, // decisão B: preço não é público
  },
  {
    path: '/whatsapp',
    title: 'Fale com um especialista | ZaperMenu',
    description: 'Responda algumas perguntas rápidas e fale com nosso time no WhatsApp.',
    robots: NOINDEX_FOLLOW,
  },
  {
    path: '/revenda/whatsapp',
    title: 'Fale com o time de revenda | ZaperMenu',
    description: 'Responda algumas perguntas rápidas e fale com o time de revenda do ZaperMenu no WhatsApp.',
    robots: NOINDEX_FOLLOW,
  },
  {
    path: '/checkout',
    title: 'Criar sua conta | ZaperMenu',
    description: 'Criação de conta no ZaperMenu.',
    robots: NOINDEX_NOFOLLOW,
  },
  {
    path: '/pagamento',
    title: 'Pagamento | ZaperMenu',
    description: 'Finalize a contratação do ZaperMenu.',
    robots: NOINDEX_NOFOLLOW,
  },
  {
    path: '/confirmacao',
    title: 'Confirmação | ZaperMenu',
    description: 'Confirmação da sua contratação do ZaperMenu.',
    robots: NOINDEX_NOFOLLOW,
  },
  {
    path: '/preview',
    title: 'Seu cardápio de demonstração | ZaperMenu',
    description: 'Prévia do seu cardápio digital ZaperMenu.',
    robots: NOINDEX_NOFOLLOW,
  },
  {
    path: '/obrigado',
    title: 'Obrigado! | ZaperMenu',
    description: 'Recebemos seu contato. Em breve nosso time fala com você.',
    robots: NOINDEX_NOFOLLOW,
  },
];

/**
 * Meta da rota `*` — página não encontrada. Nunca indexável e **sem canonical**:
 * canonical numa 404 só serviria para apontar o robô a uma URL que não existe.
 */
const NOT_FOUND = {
  path: '/404',
  title: 'Página não encontrada | ZaperMenu',
  description: 'A página que você procurou não existe ou foi movida.',
  robots: NOINDEX_NOFOLLOW,
  noCanonical: true,
};

/** `/tablet/` e `/tablet` são a mesma coisa; `//` e vazio viram `/`. */
function normalizePath(path) {
  if (!path || path === '/') return '/';
  return path.replace(/\/+$/, '') || '/';
}

function absoluteUrl(path) {
  return SITE.url + (path === '/' ? '/' : normalizePath(path));
}

/** Rota conhecida → sua entrada. Rota desconhecida → NOT_FOUND (noindex). */
function findRoute(path) {
  const normalized = normalizePath(path);
  return ROUTES.find(r => r.path === normalized) || NOT_FOUND;
}

/**
 * Meta pronta para consumo: já com defaults, URLs absolutas e canonical.
 * O canonical **não** leva query string — é o que impede `?ad=`, `?utm_*` e
 * `?pxl=` dos anúncios de virarem páginas duplicadas da home.
 */
function metaFor(path) {
  const route = findRoute(path);
  const image = route.image || SITE.defaultImage;

  return {
    path: route.path,
    title: route.title,
    description: route.description,
    robots: route.robots || INDEXABLE,
    canonical: route.noCanonical ? null : absoluteUrl(route.path),
    image: SITE.url + image,
    jsonLd: jsonLdFor(route),
  };
}

// ─────────────────────────── JSON-LD ───────────────────────────

function organizationLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE.name,
    legalName: SITE.legalName,
    url: SITE.url,
    logo: SITE.url + SITE.logo,
    sameAs: SITE.sameAs,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Pinhalzinho',
      addressRegion: 'SC',
      addressCountry: 'BR',
    },
  };
}

function webSiteLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE.name,
    url: SITE.url,
    inLanguage: 'pt-BR',
  };
}

/**
 * Sem `offers` (decisão B: preço não é público) e **sem `aggregateRating`**:
 * nota estruturada só com avaliação real e visível na página — inventar rende
 * ação manual do Google.
 */
function softwareApplicationLd(route) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE.name,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, Android, iOS',
    url: absoluteUrl(route.path),
    description: route.description,
    inLanguage: 'pt-BR',
    provider: { '@type': 'Organization', name: SITE.legalName, url: SITE.url },
  };
}

function faqPageLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.title,
      acceptedAnswer: { '@type': 'Answer', text: item.text },
    })),
  };
}

/** Lista de nós JSON-LD da rota (vira um único `<script>` no head). */
function jsonLdFor(route) {
  const nodes = [];

  if (route.robots && route.robots.indexOf('noindex') !== -1) return nodes;

  nodes.push(organizationLd());
  if (route.path === '/') nodes.push(webSiteLd());
  if (route.software) nodes.push(softwareApplicationLd(route));
  if (route.faq && route.faq.length) nodes.push(faqPageLd(route.faq));

  return nodes;
}

module.exports = {
  SITE,
  ROUTES,
  NOT_FOUND,
  INDEXABLE,
  absoluteUrl,
  normalizePath,
  findRoute,
  metaFor,
  jsonLdFor,
};
